import { createTheme } from '@mui/material/styles';

export interface TenantTheme {
  theme: any;
  logo: string;
  siteIcon: string;
  name: string;
  description: string;
}

// Helper function to get the correct asset path
const getAssetPath = (path: string) => {
  return process.env.NODE_ENV === 'production' ? `/smartfarm${path}` : path;
};

export const tenantThemes: Record<string, TenantTheme> = {
  default: {
    name: 'SmartFarm Default',
    description: 'Clean green theme for agricultural management',
    theme: createTheme({
      palette: {
        primary: { main: '#388e3c' },
        secondary: { main: '#1976d2' },
        background: { default: '#f5f5f5' },
      },
    }),
    logo: getAssetPath('/assets/themes/default/logo.svg'),
    siteIcon: getAssetPath('/assets/themes/default/favicon.svg'),
  },
  ocean: {
    name: 'Ocean Blue',
    description: 'Professional blue theme for aquaculture',
    theme: createTheme({
      palette: {
        primary: { main: '#0277bd' },
        secondary: { main: '#00acc1' },
        background: { default: '#f0f8ff' },
      },
    }),
    logo: getAssetPath('/assets/themes/ocean/logo.svg'),
    siteIcon: getAssetPath('/assets/themes/ocean/favicon.svg'),
  },
  sunset: {
    name: 'Sunset Orange',
    description: 'Warm orange theme for tropical farming',
    theme: createTheme({
      palette: {
        primary: { main: '#ff6f00' },
        secondary: { main: '#ff8f00' },
        background: { default: '#fff8e1' },
      },
    }),
    logo: getAssetPath('/assets/themes/sunset/logo.svg'),
    siteIcon: getAssetPath('/assets/themes/sunset/favicon.svg'),
  },
  forest: {
    name: 'Forest Green',
    description: 'Deep green theme for forestry management',
    theme: createTheme({
      palette: {
        primary: { main: '#2e7d32' },
        secondary: { main: '#558b2f' },
        background: { default: '#f1f8e9' },
      },
    }),
    logo: getAssetPath('/assets/themes/forest/logo.svg'),
    siteIcon: getAssetPath('/assets/themes/forest/favicon.svg'),
  },
  lavender: {
    name: 'Lavender Purple',
    description: 'Elegant purple theme for organic farming',
    theme: createTheme({
      palette: {
        primary: { main: '#7b1fa2' },
        secondary: { main: '#9c27b0' },
        background: { default: '#f3e5f5' },
      },
    }),
    logo: getAssetPath('/assets/themes/lavender/logo.svg'),
    siteIcon: getAssetPath('/assets/themes/lavender/favicon.svg'),
  },
  earth: {
    name: 'Earth Brown',
    description: 'Natural brown theme for soil management',
    theme: createTheme({
      palette: {
        primary: { main: '#6d4c41' },
        secondary: { main: '#8d6e63' },
        background: { default: '#efebe9' },
      },
    }),
    logo: getAssetPath('/assets/themes/earth/logo.svg'),
    siteIcon: getAssetPath('/assets/themes/earth/favicon.svg'),
  },
  sky: {
    name: 'Sky Blue',
    description: 'Light blue theme for greenhouse management',
    theme: createTheme({
      palette: {
        primary: { main: '#42a5f5' },
        secondary: { main: '#64b5f6' },
        background: { default: '#e3f2fd' },
      },
    }),
    logo: getAssetPath('/assets/themes/sky/logo.svg'),
    siteIcon: getAssetPath('/assets/themes/sky/favicon.svg'),
  },
  gold: {
    name: 'Golden Harvest',
    description: 'Premium gold theme for grain farming',
    theme: createTheme({
      palette: {
        primary: { main: '#f57c00' },
        secondary: { main: '#ffb300' },
        background: { default: '#fff8e1' },
      },
    }),
    logo: getAssetPath('/assets/themes/gold/logo.svg'),
    siteIcon: getAssetPath('/assets/themes/gold/favicon.svg'),
  },
};

// Legacy support for old theme names
export const legacyThemeMap: Record<string, string> = {
  tenantA: 'lavender',
  tenantB: 'ocean',
};

export const getThemeConfig = (tenantName: string): TenantTheme => {
  // Handle legacy theme names
  const actualThemeName = legacyThemeMap[tenantName] || tenantName;
  return tenantThemes[actualThemeName] || tenantThemes.default;
};
